<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const discordLink = ref<string>(import.meta.env.VITE_DISCORD_INVITE_LINK ?? '')

const currentYear = new Date().getFullYear()
</script>

<template>
	<footer class="relative mt-32 border-t-2 border-0 border-solid border-slate-800/50">
		<!-- Subtle background pattern -->
		<div class="absolute inset-0 opacity-[0.02]">
			<div class="h-full w-full bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.8)_1px,transparent_0)] bg-[length:24px_24px]" />
		</div>

		<div class="relative max-w-6xl mx-auto px-6 py-16">
			<!-- Main Footer Content -->
			<div class="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
				<!-- Brand Section -->
				<div class="md:col-span-1">
					<div class="flex items-center gap-1 mb-4 font-extrabold">
						<div class="text-orange-500 uppercase flex-center">
							ai
						</div>
						<span class="text-slate-200 font-bold text-lg">FapLab</span>
					</div>
					<p class="text-slate-400 text-sm leading-relaxed max-w-xs">
						{{ t('footer.description') }}
					</p>
				</div>

				<!-- Services -->
				<div>
					<h3 class="text-slate-200 font-semibold text-sm mb-4 tracking-wide uppercase">
						{{ t('footer.services') }}
					</h3>
					<ul class="space-y-3">
						<li>
							<a
								href="/ai-hentai-generator"
								class="text-slate-400 hover:text-purple-400 text-sm transition-colors duration-200 flex items-center gap-2 group"
								target="_blank"
							>
								<span>AI Hentai Generator</span>
								<i class="pi pi-external-link text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
							</a>
						</li>
						<li>
							<a
								href="/ai-realistic-generator"
								class="text-slate-400 hover:text-purple-400 text-sm transition-colors duration-200 flex items-center gap-2 group"
								target="_blank"
							>
								<span>AI Realistic Generator</span>
								<i class="pi pi-external-link text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
							</a>
						</li>
					</ul>
				</div>

				<!-- Platform -->
				<div>
					<h3 class="text-slate-200 font-semibold text-sm mb-4 tracking-wide uppercase">
						{{ t('footer.platform') }}
					</h3>
					<ul class="space-y-3">
						<li>
							<router-link
								to="/tickets"
								title="View FapLab Ticket Prices and Purchase Options"
								class="text-slate-400 hover:text-purple-400 text-sm no-underline transition-colors duration-200"
							>
								{{ t('menu.shop') }}
							</router-link>
						</li>
						<li>
							<router-link
								to="/guide"
								title="Learn How to Generate AI Images with FapLab"
								class="text-slate-400 hover:text-purple-400 text-sm no-underline transition-colors duration-200"
							>
								{{ t('common.guide') }}
							</router-link>
						</li>
						<li>
							<router-link
								to="/leaderboard"
								title="View the FapLab Leaderboard and Top Users"
								class="text-slate-400 hover:text-purple-400 text-sm no-underline transition-colors duration-200"
							>
								{{ t('menu.leaderboard') }}
							</router-link>
						</li>
						<li>
							<router-link
								to="/about"
								title="View the FapLab Leaderboard and Top Users"
								class="text-slate-400 hover:text-purple-400 text-sm no-underline transition-colors duration-200"
							>
								{{ t('menu.about') }}
							</router-link>
						</li>
					</ul>
				</div>

				<!-- Community -->
				<div>
					<h3 class="text-slate-200 font-semibold text-sm mb-4 tracking-wide uppercase">
						{{ t('footer.community') }}
					</h3>
					<ul class="space-y-3">
						<li>
							<a
								:href="discordLink"
								class="text-slate-400 no-underline hover:text-indigo-400 text-sm transition-colors duration-200 flex items-center gap-2 group"
								target="_blank"
							>
								<i class="pi pi-discord text-base" />
								<span>Discord</span>
							</a>
						</li>
						<li>
							<a
								href="https://www.reddit.com/r/FapFapAI/"
								class="text-slate-400 w-fit no-underline hover:text-orange-400 text-sm transition-colors duration-200 flex items-center gap-2 group"
								target="_blank"
							>
								<i class="pi pi-reddit text-base" />
								<span>Reddit</span>
							</a>
						</li>
						<li>
							<a
								href="https://x.com/ai_faplab"
								class="text-slate-400 w-fit no-underline hover:text-slate-200 text-sm transition-colors duration-200 flex items-center gap-2 group"
								target="_blank"
							>
								<i class="pi pi-twitter text-base" />
								<span>X (Twitter)</span>
							</a>
						</li>
						<li>
							<a
								href="https://www.tiktok.com/@ai.faplab"
								class="text-slate-400 w-fit no-underline hover:text-pink-400 text-sm transition-colors duration-200 flex items-center gap-2 group"
								target="_blank"
							>
								<i class="pi pi-tiktok text-base" />
								<span>TikTok</span>
							</a>
						</li>
					</ul>
				</div>
			</div>

			<!-- Divider -->
			<div class="border-t border-slate-800/50 pt-8">
				<div class="flex flex-col md:flex-row justify-between items-center gap-4">
					<!-- Legal Links -->
					<div class="flex flex-wrap justify-center md:justify-start gap-6 text-xs">
						<a
							href="/legal/PrivacyPolicy.en.pdf"
							target="_blank"
							class="text-slate-500 hover:text-slate-300 transition-colors duration-200"
						>
							{{ t('legal.privacy_policy') }}
						</a>
						<a
							href="/legal/TOS.en.pdf"
							target="_blank"
							class="text-slate-500 hover:text-slate-300 transition-colors duration-200"
						>
							{{ t('legal.tos') }}
						</a>
						<a
							href="/legal/DMCA_Policy.en.pdf"
							class="text-slate-500 hover:text-slate-300 transition-colors duration-200 flex items-center gap-1"
						>
							{{ t('legal.dmca') }}
						</a>
						<a
							href="mailto:contact@ai-faplab.com"
							class="text-slate-500 hover:text-slate-300 transition-colors duration-200 flex items-center gap-1"
						>
							<i class="pi pi-envelope text-xs" />
							<span>Contact</span>
						</a>
					</div>

					<!-- Copyright -->
					<div class="text-slate-500 text-xs text-center md:text-right">
						<p>© {{ currentYear }} AIFapLab. All rights reserved.</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Subtle gradient overlay at bottom -->
		<div class="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
	</footer>
</template>

<style scoped>
/* Custom hover effects for social links */
.group:hover i {
	transform: scale(1.1);
	transition: transform 0.2s ease;
}

/* Smooth transitions for all interactive elements */
a {
	position: relative;
}

a::after {
	content: '';
	position: absolute;
	bottom: -2px;
	left: 0;
	width: 0;
	height: 1px;
	background: currentColor;
	transition: width 0.3s ease;
}

a:hover::after {
	width: 100%;
}

/* Remove underline effect for social links with icons */
a:has(i)::after {
	display: none;
}
</style>
